// src/pages/retiros/RetirosPage.tsx

import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../hooks/redux";
import {
  fetchRetiros,
  aprobarRetiroThunk,
  rechazarRetiroThunk,
  procesarRetiroThunk,
  setFiltroEstado,
  setPage,
  clearMessages,
} from "../../store/slices/retirosSlice";
import { Retiro, EstadoRetiro } from "../../types";

// ── helpers ──────────────────────────────────────────────────────────────────

const fmt = (n: number) =>
  new Intl.NumberFormat("es-CO", {
    style: "currency",
    currency: "COP",
    maximumFractionDigits: 0,
  }).format(n);

const formatDate = (d?: string) => {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("es-CO", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const ESTADOS: { value: string; label: string; icon: string }[] = [
  { value: "", label: "Todos", icon: "bi-list-ul" },
  { value: "PENDIENTE", label: "Pendientes", icon: "bi-hourglass-split" },
  { value: "APROBADO", label: "Aprobados", icon: "bi-check-circle" },
  { value: "RECHAZADO", label: "Rechazados", icon: "bi-x-circle" },
  { value: "PROCESADO", label: "Procesados", icon: "bi-bank" },
];

// ── Modal de confirmación ─────────────────────────────────────────────────────

interface ModalProps {
  retiro: Retiro;
  tipo: "aprobar" | "rechazar" | "procesar";
  onConfirm: (obs?: string) => void;
  onClose: () => void;
  loading: boolean;
}

function ConfirmModal({
  retiro,
  tipo,
  onConfirm,
  onClose,
  loading,
}: ModalProps) {
  const [obs, setObs] = useState("");

  const config = {
    aprobar: {
      title: "Aprobar retiro",
      icon: "bi-check-circle-fill",
      color: "var(--fg-green-light)",
      btnClass: "btn-fg-success",
      btnText: "Confirmar",
    },
    rechazar: {
      title: "Rechazar retiro",
      icon: "bi-x-circle-fill",
      color: "var(--fg-red)",
      btnClass: "btn-fg-danger",
      btnText: "Confirmar rechazo",
    },
    procesar: {
      title: "Marcar como procesado",
      icon: "bi-bank",
      color: "var(--fg-blue)",
      btnClass: "btn-fg-info",
      btnText: "Confirmar transferencia",
    },
  }[tipo];

  return (
    <div
      className="modal-overlay"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="modal-box">
        <div className="modal-title">
          <i
            className={`bi ${config.icon}`}
            style={{ color: config.color, fontSize: "1.3rem" }}
          />
          {config.title}
        </div>

        {/* Detalle del retiro */}
        <div
          style={{
            background: "#f8fafb",
            borderRadius: 10,
            padding: "14px 16px",
            marginBottom: 16,
            fontSize: "0.875rem",
            lineHeight: 1.8,
          }}
        >
          <div>
            <strong>Monto:</strong>{" "}
            <span className="money-value">{fmt(retiro.monto)}</span>
          </div>
          <div>
            <strong>Titular:</strong> {retiro.titular || "—"}
          </div>
          <div>
            <strong>Banco:</strong> {retiro.banco || "—"}
          </div>
          <div>
            <strong>Tipo cuenta:</strong> {retiro.tipoCuenta || "—"}
          </div>
          <div>
            <strong>Número:</strong> <code>{retiro.numeroCuenta || "—"}</code>
          </div>
          <div>
            <strong>MVZ ID:</strong>{" "}
            <code style={{ fontSize: "0.75rem" }}>{retiro.mvzId}</code>
          </div>
        </div>

        {/* Aviso especial para CONTRAENTREGA */}
        <div
          className="alert alert-info d-flex align-items-start gap-2 py-2 mb-3"
          style={{ borderRadius: 8, fontSize: "0.82rem" }}
        >
          <i className="bi bi-info-circle-fill mt-1" />
          <span>
            Este retiro corresponde a fondos acumulados en la wallet del MVZ
            (incluyendo servicios <strong>CONTRAENTREGA</strong>). La aprobación
            implica realizar la transferencia bancaria al titular.
          </span>
        </div>

        {/* Observación (opcional para todos) */}
        {(tipo === "rechazar" || tipo === "aprobar") && (
          <div className="mb-2">
            <label
              style={{
                fontSize: "0.82rem",
                fontWeight: 600,
                marginBottom: 4,
                display: "block",
              }}
            >
              Observación {tipo === "rechazar" ? "(requerida)" : "(opcional)"}
            </label>
            <textarea
              className="form-control"
              rows={2}
              placeholder={
                tipo === "rechazar"
                  ? "Motivo del rechazo..."
                  : "Nota adicional..."
              }
              value={obs}
              onChange={(e) => setObs(e.target.value)}
              style={{ fontSize: "0.85rem", borderRadius: 8 }}
            />
          </div>
        )}

        <div className="modal-actions">
          <button
            className="btn btn-outline-secondary btn-sm"
            onClick={onClose}
            disabled={loading}
          >
            Cancelar
          </button>
          <button
            className={config.btnClass}
            onClick={() => onConfirm(obs || undefined)}
            disabled={loading || (tipo === "rechazar" && !obs.trim())}
          >
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm" />{" "}
                Procesando...
              </>
            ) : (
              <>
                <i className={`bi ${config.icon}`} /> {config.btnText}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Row de retiro ─────────────────────────────────────────────────────────────

function RetiroRow({
  retiro,
  actionLoading,
  onAprobar,
  onRechazar,
  onProcesar,
  onDetail,
}: {
  retiro: Retiro;
  actionLoading: string | null;
  onAprobar: (r: Retiro) => void;
  onRechazar: (r: Retiro) => void;
  onProcesar: (r: Retiro) => void;
  onDetail: (r: Retiro) => void;
}) {
  const isLoading = actionLoading === retiro.id;

  return (
    <tr>
      <td>
        <button
          className="btn btn-link p-0 text-decoration-none"
          style={{
            fontSize: "0.75rem",
            fontFamily: "monospace",
            color: "var(--fg-green)",
          }}
          onClick={() => onDetail(retiro)}
          title="Ver detalle"
        >
          {retiro.id.slice(0, 8)}...
        </button>
      </td>
      <td>
        <div
          style={{
            fontSize: "0.78rem",
            color: "var(--fg-muted)",
            fontFamily: "monospace",
          }}
        >
          {retiro.mvzId?.slice(0, 8)}...
        </div>
      </td>
      <td>
        <span className="money-value" style={{ fontSize: "1rem" }}>
          {fmt(retiro.monto)}
        </span>
      </td>
      <td>
        <div style={{ fontWeight: 600, fontSize: "0.85rem" }}>
          {retiro.titular || "—"}
        </div>
        <div className="text-muted-fg">{retiro.tipoCuenta}</div>
      </td>
      <td>
        <div style={{ fontWeight: 500, fontSize: "0.85rem" }}>
          {retiro.banco || "—"}
        </div>
        <div className="text-muted-fg">{retiro.numeroCuenta}</div>
      </td>
      <td>
        <span className={`badge-estado badge-${retiro.estado}`}>
          <i
            className={`bi ${
              retiro.estado === "PENDIENTE"
                ? "bi-hourglass-split"
                : retiro.estado === "APROBADO"
                  ? "bi-check-circle"
                  : retiro.estado === "RECHAZADO"
                    ? "bi-x-circle"
                    : "bi-bank"
            }`}
          />
          {retiro.estado}
        </span>
      </td>
      <td className="text-muted-fg">{formatDate(retiro.createdDate)}</td>
      <td>
        <div className="table-action-group">
          {retiro.estado === "PENDIENTE" && (
            <>
              <button
                className="btn-fg-success"
                onClick={() => onAprobar(retiro)}
                disabled={isLoading}
                title="Aprobar retiro"
              >
                {isLoading ? (
                  <span className="spinner-border spinner-border-sm" />
                ) : (
                  <>
                    <i className="bi bi-check-lg" /> Aprobar
                  </>
                )}
              </button>
              <button
                className="btn-fg-danger"
                onClick={() => onRechazar(retiro)}
                disabled={isLoading}
                title="Rechazar retiro"
              >
                <i className="bi bi-x-lg" /> Rechazar
              </button>
            </>
          )}
          {retiro.estado === "APROBADO" && (
            <button
              className="btn-fg-info"
              onClick={() => onProcesar(retiro)}
              disabled={isLoading}
              title="Marcar como transferido"
            >
              {isLoading ? (
                <span className="spinner-border spinner-border-sm" />
              ) : (
                <>
                  <i className="bi bi-bank" /> Procesado
                </>
              )}
            </button>
          )}
          {(retiro.estado === "RECHAZADO" || retiro.estado === "PROCESADO") && (
            <button className="btn-fg-outline" onClick={() => onDetail(retiro)}>
              <i className="bi bi-eye" /> Ver
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}

// ── PÁGINA PRINCIPAL ──────────────────────────────────────────────────────────

export default function RetirosPage() {
  const dispatch = useAppDispatch();
  const {
    items,
    loading,
    actionLoading,
    error,
    successMsg,
    filtroEstado,
    currentPage,
    totalPages,
    totalElements,
  } = useAppSelector((s) => s.retiros);

  const [searchParams] = useSearchParams();
  const [modal, setModal] = useState<{
    retiro: Retiro;
    tipo: "aprobar" | "rechazar" | "procesar";
  } | null>(null);
  const [detailRetiro, setDetailRetiro] = useState<Retiro | null>(null);

  // Leer filtro de la URL
  useEffect(() => {
    const estado = searchParams.get("estado") || "";
    dispatch(setFiltroEstado(estado));
  }, [searchParams, dispatch]);

  // Cargar retiros cuando cambia filtro o página
  const load = useCallback(() => {
    dispatch(
      fetchRetiros({ estado: filtroEstado || undefined, page: currentPage }),
    );
  }, [dispatch, filtroEstado, currentPage]);

  useEffect(() => {
    load();
  }, [load]);

  // Limpiar mensajes después de 3s
  useEffect(() => {
    if (successMsg || error) {
      const t = setTimeout(() => dispatch(clearMessages()), 3000);
      return () => clearTimeout(t);
    }
  }, [successMsg, error, dispatch]);

  // ── Acciones ──────────────────────────────────────────────────────────────

  const handleConfirm = async (obs?: string) => {
    if (!modal) return;
    const { retiro, tipo } = modal;
    if (tipo === "aprobar") {
      await dispatch(aprobarRetiroThunk({ id: retiro.id, observacion: obs }));
    } else if (tipo === "rechazar") {
      await dispatch(rechazarRetiroThunk({ id: retiro.id, observacion: obs }));
    } else {
      await dispatch(procesarRetiroThunk(retiro.id));
    }
    setModal(null);
  };

  const fmt2 = (n: number) =>
    new Intl.NumberFormat("es-CO", {
      style: "currency",
      currency: "COP",
      maximumFractionDigits: 0,
    }).format(n);

  return (
    <>
      {/* Header */}
      <div className="page-header d-flex align-items-start justify-content-between flex-wrap gap-3">
        <div>
          <h1 className="page-title">
            <i
              className="bi bi-cash-coin me-2"
              style={{ color: "var(--fg-green)", fontSize: "1.2rem" }}
            />
            Gestión de Retiros
          </h1>
          <p className="page-subtitle">
            Aprueba, rechaza y procesa las solicitudes de retiro de los usuarios
            MVZ.
            <strong style={{ color: "var(--fg-orange)" }}>
              {" "}
              Todos los retiros
            </strong>{" "}
            (incluidos los de servicios CONTRAENTREGA) requieren aprobación
            aquí.
          </p>
        </div>
        <button className="btn-fg-primary" onClick={load}>
          <i className="bi bi-arrow-clockwise" /> Actualizar
        </button>
      </div>

      {/* Alertas */}
      {successMsg && (
        <div
          className="alert alert-success d-flex align-items-center gap-2 mb-3"
          style={{ borderRadius: 10 }}
        >
          <i className="bi bi-check-circle-fill" /> {successMsg}
        </div>
      )}
      {error && (
        <div
          className="alert alert-danger d-flex align-items-center gap-2 mb-3"
          style={{ borderRadius: 10 }}
        >
          <i className="bi bi-exclamation-triangle-fill" /> {error}
        </div>
      )}

      {/* Filtros */}
      <div className="fg-card mb-3">
        <div style={{ padding: "14px 20px" }}>
          <div className="filter-bar">
            <span
              style={{
                fontSize: "0.82rem",
                fontWeight: 600,
                color: "var(--fg-muted)",
              }}
            >
              <i className="bi bi-funnel me-1" />
              Filtrar:
            </span>
            {ESTADOS.map((e) => (
              <button
                key={e.value}
                className={
                  filtroEstado === e.value ? "btn-fg-primary" : "btn-fg-outline"
                }
                style={{ padding: "6px 14px", fontSize: "0.82rem" }}
                onClick={() => dispatch(setFiltroEstado(e.value))}
              >
                <i className={`bi ${e.icon}`} /> {e.label}
              </button>
            ))}
            <span className="ms-auto text-muted-fg">
              {totalElements} resultado{totalElements !== 1 ? "s" : ""}
            </span>
          </div>
        </div>
      </div>

      {/* Tabla */}
      <div className="fg-card">
        {loading ? (
          <div
            className="d-flex align-items-center justify-content-center"
            style={{ minHeight: 200 }}
          >
            <div
              className="spinner-border"
              style={{ color: "var(--fg-green)" }}
            />
          </div>
        ) : items.length === 0 ? (
          <div className="empty-state">
            <i className="bi bi-inbox" />
            <p>No hay retiros con el filtro seleccionado</p>
          </div>
        ) : (
          <>
            <div className="table-responsive">
              <table className="fg-table">
                <thead>
                  <tr>
                    <th>ID Retiro</th>
                    <th>MVZ</th>
                    <th>Monto</th>
                    <th>Titular</th>
                    <th>Banco / Cuenta</th>
                    <th>Estado</th>
                    <th>Fecha</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((r) => (
                    <RetiroRow
                      key={r.id}
                      retiro={r}
                      actionLoading={actionLoading}
                      onAprobar={(r) =>
                        setModal({ retiro: r, tipo: "aprobar" })
                      }
                      onRechazar={(r) =>
                        setModal({ retiro: r, tipo: "rechazar" })
                      }
                      onProcesar={(r) =>
                        setModal({ retiro: r, tipo: "procesar" })
                      }
                      onDetail={setDetailRetiro}
                    />
                  ))}
                </tbody>
              </table>
            </div>

            {/* Paginación */}
            <div className="pagination-bar">
              <span>
                Página {currentPage + 1} de {Math.max(totalPages, 1)} ·{" "}
                {totalElements} retiros
              </span>
              <div style={{ display: "flex", gap: 6 }}>
                <button
                  className="btn btn-sm btn-outline-secondary"
                  disabled={currentPage === 0}
                  onClick={() => dispatch(setPage(currentPage - 1))}
                  style={{ borderRadius: 6 }}
                >
                  <i className="bi bi-chevron-left" />
                </button>
                <button
                  className="btn btn-sm btn-outline-secondary"
                  disabled={currentPage >= totalPages - 1}
                  onClick={() => dispatch(setPage(currentPage + 1))}
                  style={{ borderRadius: 6 }}
                >
                  <i className="bi bi-chevron-right" />
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Modal de acción */}
      {modal && (
        <ConfirmModal
          retiro={modal.retiro}
          tipo={modal.tipo}
          onConfirm={handleConfirm}
          onClose={() => setModal(null)}
          loading={actionLoading === modal.retiro.id}
        />
      )}

      {/* Modal de detalle */}
      {detailRetiro && (
        <div
          className="modal-overlay"
          onClick={(e) => e.target === e.currentTarget && setDetailRetiro(null)}
        >
          <div className="modal-box">
            <div className="modal-title">
              <i
                className="bi bi-file-text-fill"
                style={{ color: "var(--fg-green)" }}
              />
              Detalle del retiro
            </div>
            <div style={{ fontSize: "0.875rem", lineHeight: 2 }}>
              <div>
                <strong>ID:</strong>{" "}
                <code style={{ fontSize: "0.75rem" }}>{detailRetiro.id}</code>
              </div>
              <div>
                <strong>MVZ ID:</strong>{" "}
                <code style={{ fontSize: "0.75rem" }}>
                  {detailRetiro.mvzId}
                </code>
              </div>
              <div>
                <strong>Monto:</strong>{" "}
                <span className="money-value">{fmt2(detailRetiro.monto)}</span>
              </div>
              <div>
                <strong>Estado:</strong>{" "}
                <span className={`badge-estado badge-${detailRetiro.estado}`}>
                  {detailRetiro.estado}
                </span>
              </div>
              <div>
                <strong>Banco:</strong> {detailRetiro.banco || "—"}
              </div>
              <div>
                <strong>Tipo cuenta:</strong> {detailRetiro.tipoCuenta || "—"}
              </div>
              <div>
                <strong>Número cuenta:</strong>{" "}
                <code>{detailRetiro.numeroCuenta || "—"}</code>
              </div>
              <div>
                <strong>Titular:</strong> {detailRetiro.titular || "—"}
              </div>
              {detailRetiro.observacion && (
                <div>
                  <strong>Observación:</strong> {detailRetiro.observacion}
                </div>
              )}
              <div>
                <strong>Creado:</strong> {formatDate(detailRetiro.createdDate)}
              </div>
              <div>
                <strong>Actualizado:</strong>{" "}
                {formatDate(detailRetiro.lastModifiedDate)}
              </div>
            </div>
            <div className="modal-actions">
              <button
                className="btn-fg-primary"
                onClick={() => setDetailRetiro(null)}
              >
                <i className="bi bi-x-lg" /> Cerrar
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
